import React, { useState, useMemo } from 'react';
import classNames from 'classnames';
import {
    AreaChart,
    Area,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer
} from 'recharts';

// --- Constants for Padded Data Ordering ---
const DAYS_OF_WEEK = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const MONTHS_OF_YEAR = [
    { name: "Jan", month: "2025-01" },
    { name: "Feb", month: "2025-02" },
    { name: "Mar", month: "2025-03" },
    { name: "Apr", month: "2025-04" },
    { name: "May", "month": "2025-05" },
    { name: "Jun", month: "2025-06" },
    { name: "Jul", month: "2025-07" },
    { name: "Aug", month: "2025-08" },
    { name: "Sep", month: "2025-09" },
    { name: "Oct", month: "2025-10" },
    { name: "Nov", month: "2025-11" },
    { name: "Dec", month: "2025-12" }
];

// --- Data Padding Helpers ---

// Pads weekly data to ensure all 7 days are present
const padWeeklyData = (weeklyGraph) => {
    if(!weeklyGraph) return [];
    const existingDataMap = weeklyGraph?.reduce((acc, item) => {
        acc[item.day] = item.count;
        return acc;
    }, {});

    const paddedData = DAYS_OF_WEEK.map(day => {
        const existingItem = weeklyGraph.find(item => item.day === day);
        return {
            day: day,
            count: existingDataMap[day] || 0,
            date: existingItem ? existingItem.date : null
        };
    });
    return paddedData;
};

// Pads monthly data to ensure all 12 months are present (Jan...Dec)
const padMonthlyData = (monthlyGraph) => {
    if(!monthlyGraph) return [];
    // 1. Create a map for quick lookup by monthName (e.g., "Nov")
    const existingDataMap = monthlyGraph?.reduce((acc, item) => {
        acc[item.monthName] = item.count;
        return acc;
    }, {});

    // 2. Iterate over the standard months and create a complete array
    const paddedData = MONTHS_OF_YEAR.map(monthItem => {
        const monthName = monthItem.name;
        return {
            month: monthItem.month, // The full date string
            monthName: monthName,    // The short name for the X-axis
            count: existingDataMap[monthName] || 0, // Count from data, or 0
        };
    });
    
    return paddedData;
};

// --- Recharts Helpers ---

// Function to format the Y-Axis tick label to display 'k' for thousands
const formatYAxis = (tick) => {
    if (tick >= 1000) {
        return `${tick / 1000}k`;
    }
    return tick;
};

// Custom Tooltip component for Recharts
const CustomTooltip = ({ active, payload, selected }) => {
    if (active && payload && payload.length) {
        const data = payload[0].payload;
        // Determine the label based on the selected view
        const displayLabel = selected === 'weekly' ? data.day : data.monthName;
        return (
            <div className="bg-white p-2 border border-gray-200 rounded-md shadow-lg text-sm">
                <p className="font-semibold text-gray-700">{displayLabel}</p>
                <p className="text-gray-600">Count: <span style={{ color: payload[0].stroke }} className="font-bold">{payload[0].value}</span></p>
            </div>
        );
    }
    return null;
};

// --- Main ChartContainer Component ---
const ChartContainer = ({
    title = "Bookings",
    totalCount = 0,
    chartColor = "#4CAF50",
    chartData = { weeklyGraph: [], monthlyGraph: [] },
}) => {

    const [selected, setSelected] = useState('weekly');

    // Calculate padded data using useMemo for performance
    const paddedWeeklyData = useMemo(() => padWeeklyData(chartData.weeklyGraph), [chartData.weeklyGraph]);
    const paddedMonthlyData = useMemo(() => padMonthlyData(chartData.monthlyGraph), [chartData.monthlyGraph]);

    // Select the appropriate data array
    const data = selected === 'weekly' ? paddedWeeklyData : paddedMonthlyData;

    // Define the key for the X-Axis based on the selection
    const xAxisKey = selected === 'weekly' ? 'day' : 'monthName';

    // The data key for the count value
    const dataKey = 'count';

    return (
        <div className='flex flex-col gap-y-3'>
            <h4 className="text-lg font-semibold text-gray-800">{title}</h4>
            <div className="bg-white px-4 py-6 rounded-2xl col-span-1 shadow-[0_8px_24px_rgba(0,0,0,0.08)]">
                <div className="flex justify-between items-center mb-4">
                    {/* Weekly / Monthly Toggle */}
                    <div>
                        <span className="text-m text-gray-700">
                            Total Count:
                        </span>
                        <span className="text-m text-black text-medium">
                            {" "+totalCount}
                        </span>
                    </div>
                    <div className="flex space-x-1 border border-gray-200 rounded-lg p-1 text-xs text-gray-600">
                        <button
                            onClick={() => setSelected("weekly")}
                            className={classNames(
                                "px-2 py-1 rounded-md transition",
                                selected === "weekly" ? "text-black" : "text-gray-600"
                            )}
                        >
                            Weekly
                        </button>

                        <button
                            onClick={() => setSelected("monthly")}
                            className={classNames(
                                "px-2 py-1 rounded-md transition",
                                selected === "monthly" ? "text-black" : "text-gray-600"
                            )}
                        >
                            Monthly
                        </button>
                    </div>
                </div>

                {/* Recharts Area Chart */}
                <div className="h-[271px] w-full">
                    <ResponsiveContainer width="100%" height="100%">
                        <AreaChart
                            data={data}
                            margin={{
                                top: 10,
                                right: 20,
                                left: 20,
                                bottom: 0
                            }}
                        >
                            <defs>
                                <linearGradient id="colorCount" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor={chartColor} stopOpacity={1} />
                                    <stop offset="95%" stopColor={chartColor} stopOpacity={0} />
                                </linearGradient>
                            </defs>

                            {/* X-Axis: Displays day or month name */}
                            <XAxis
                                dataKey={xAxisKey}
                                stroke="#809FB8"
                                tickLine={false}
                                axisLine={false}
                                tick={{ dy: 10 }}
                                className="text-xs text-gray-500"
                            />

                            <YAxis
                                tickFormatter={formatYAxis}
                                stroke="#809FB8"
                                tickLine={false}
                                axisLine={false}
                                width={26}
                                tick={{ dx: -15 }}
                                className="text-xs text-gray-500"
                                domain={[0, 'auto']}
                            />

                            <Tooltip content={<CustomTooltip selected={selected} />} />

                            <Area
                                type="monotone"
                                dataKey={dataKey}
                                stroke={chartColor}
                                fill="url(#colorCount)"
                                strokeWidth={2}
                            />
                        </AreaChart>
                    </ResponsiveContainer>
                </div>
            </div>
        </div>
    );
};

export default ChartContainer;